import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

import { Checkbox } from '../../../components/ui/Checkbox';

const PhotoUploadSection = ({ formData, errors, onChange, onPrivacyChange }) => {
  const [photos, setPhotos] = useState(formData?.photos || []);
  const [blurIntensity, setBlurIntensity] = useState(formData?.blurIntensity || 50);
  const [showPrivacyControls, setShowPrivacyControls] = useState(false);
  const maxPhotos = 6;

  const handlePhotoUpload = (e) => {
    const files = Array.from(e?.target?.files);
    const remainingSlots = maxPhotos - photos?.length;
    
    if (files?.length > remainingSlots) {
      alert(`You can only upload ${remainingSlots} more photo(s)`);
      return;
    }

    const newPhotos = files?.map(file => ({
      id: Date.now() + Math.random(),
      url: URL.createObjectURL(file),
      file: file,
      alt: `Profile photo showing ${file?.name?.split('.')?.[0]}`
    }));

    const updatedPhotos = [...photos, ...newPhotos];
    setPhotos(updatedPhotos);
    onChange({ target: { name: 'photos', value: updatedPhotos } });
  };

  const handlePhotoRemove = (photoId) => {
    const updatedPhotos = photos?.filter(p => p?.id !== photoId);
    setPhotos(updatedPhotos);
    onChange({ target: { name: 'photos', value: updatedPhotos } });
  };

  const handleBlurChange = (e) => {
    const value = parseInt(e?.target?.value);
    setBlurIntensity(value);
    onPrivacyChange('blurIntensity', value);
  };

  const getBlurClass = () => {
    if (blurIntensity < 30) return '';
    if (blurIntensity < 60) return 'blur-sm';
    if (blurIntensity < 80) return 'blur-md';
    return 'blur-lg';
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 shadow-warm">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div>
          <h3 className="text-lg md:text-xl font-heading font-semibold mb-1">Photos</h3>
          <p className="text-sm text-muted-foreground">
            {photos?.length}/{maxPhotos} photos uploaded
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowPrivacyControls(!showPrivacyControls)}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm font-caption font-medium transition-smooth hover:bg-primary/20"
        >
          <Icon name="Eye" size={16} />
          Privacy
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-4">
        {photos?.map((photo) => (
          <div key={photo?.id} className="relative aspect-[3/4] rounded-lg overflow-hidden bg-muted group">
            <Image
              src={photo?.url}
              alt={photo?.alt}
              className={`w-full h-full object-cover ${getBlurClass()}`}
            />
            <button
              type="button"
              onClick={() => handlePhotoRemove(photo?.id)}
              className="absolute top-2 right-2 p-1.5 bg-error rounded-full opacity-0 group-hover:opacity-100 transition-smooth"
            >
              <Icon name="X" size={16} color="#FFFFFF" />
            </button>
          </div>
        ))}

        {photos?.length < maxPhotos && (
          <label className="relative aspect-[3/4] rounded-lg border-2 border-dashed border-border bg-muted hover:bg-muted/80 cursor-pointer transition-smooth flex flex-col items-center justify-center gap-2">
            <Icon name="Plus" size={32} color="var(--color-muted-foreground)" />
            <span className="text-xs md:text-sm font-caption font-medium text-muted-foreground">
              Add Photo
            </span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </label>
        )}
      </div>
      {showPrivacyControls && (
        <div className="p-4 bg-muted rounded-lg space-y-4 animate-slide-down">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Shield" size={20} color="var(--color-primary)" />
            <h4 className="text-sm font-caption font-semibold">Photo Privacy Controls</h4>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-caption font-medium">Blur Intensity</label>
              <span className="text-sm font-mono text-primary">{blurIntensity}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={blurIntensity}
              onChange={handleBlurChange}
              className="w-full h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Clear</span>
              <span>Moderate</span>
              <span>Heavy</span>
            </div>
          </div>

          <Checkbox
            label="Hide face in photos"
            description="Automatically blur facial features for maximum privacy"
            checked={formData?.hideFace}
            onChange={(e) => onPrivacyChange('hideFace', e?.target?.checked)}
          />

          <Checkbox
            label="Require approval before viewing"
            description="Others must request permission to see your photos"
            checked={formData?.requirePhotoApproval}
            onChange={(e) => onPrivacyChange('requirePhotoApproval', e?.target?.checked)}
          />

          <div className="p-3 bg-success/10 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs text-foreground">
                Your photos are stored securely and only visible according to your privacy settings
              </p>
            </div>
          </div>
        </div>
      )}
      {errors?.photos && (
        <p className="mt-4 text-sm text-error">{errors?.photos}</p>
      )}
      <div className="mt-4 p-4 bg-primary/5 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Camera" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-caption font-semibold mb-1">Photo Guidelines</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Upload clear, recent photos of yourself</li>
              <li>• Avoid group photos or images with others</li>
              <li>• Use privacy controls to protect your identity</li>
              <li>• Photos must comply with community guidelines</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUploadSection;